// src/components/ui/AnexosUpload.jsx
import React, { useRef, useState } from "react";

const ICONS = {
  "application/pdf":        "bi-file-earmark-pdf-fill",
  "image/jpeg":             "bi-file-earmark-image-fill",
  "image/png":              "bi-file-earmark-image-fill",
  "application/msword":     "bi-file-earmark-word-fill",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "bi-file-earmark-word-fill",
  "application/vnd.ms-excel": "bi-file-earmark-excel-fill",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "bi-file-earmark-excel-fill",
  "text/plain":             "bi-file-earmark-text-fill",
};
const COLORS = {
  "application/pdf": "#EF4444",
  "image/":          "#3B82F6",
  "application/vnd.openxmlformats-officedocument.wordprocessingml": "#1D4ED8",
  "application/vnd.ms-word": "#1D4ED8",
  "application/vnd.openxmlformats-officedocument.spreadsheetml": "#166534",
  "application/vnd.ms-excel": "#166534",
};

function getIcon(tpArquivo) {
  if (!tpArquivo) return "bi-file-earmark-fill";
  return ICONS[tpArquivo] || "bi-file-earmark-fill";
}
function getColor(tpArquivo) {
  if (!tpArquivo) return "var(--gray-500)";
  for (const [k,v] of Object.entries(COLORS)) {
    if (tpArquivo.startsWith(k)) return v;
  }
  return "var(--gray-500)";
}
function fmtSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024*1024) return `${(bytes/1024).toFixed(1)} KB`;
  return `${(bytes/1024/1024).toFixed(1)} MB`;
}

/**
 * AnexosUpload — componente reutilizável de anexos múltiplos
 *
 * Props:
 *   anexos:          array de {id?, nmArquivo, tpArquivo, tamanho, base64?}
 *   onAdd:           (anexo) => void  — chamado com o novo anexo (com base64)
 *   onRemove:        (id|index) => void
 *   onDownload:      (anexo) => void  — para anexos salvos (com id)
 *   maxSizeMB:       number (default 10)
 *   accept:          string (default "*")
 *   label:           string
 *   disabled:        bool
 */
export default function AnexosUpload({
  anexos = [],
  onAdd,
  onRemove,
  onDownload,
  maxSizeMB = 10,
  accept = "*",
  label = "Anexar Arquivo",
  disabled = false,
}) {
  const inputRef  = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [loading,  setLoading]  = useState(false);

  const processFile = async (file) => {
    if (file.size > maxSizeMB * 1024 * 1024) {
      alert(`Arquivo "${file.name}" excede o tamanho máximo de ${maxSizeMB}MB.`);
      return;
    }
    setLoading(true);
    try {
      const base64 = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload  = () => res(reader.result.split(",")[1]);
        reader.onerror = () => rej(new Error("Erro ao ler arquivo."));
        reader.readAsDataURL(file);
      });
      onAdd?.({ nmArquivo: file.name, tpArquivo: file.type, tamanho: file.size, base64 });
    } catch(e) { alert(e.message); }
    setLoading(false);
  };

  const handleFiles = async (files) => {
    for (const file of Array.from(files)) await processFile(file);
  };

  const handleDownload = (anexo) => {
    if (onDownload) { onDownload(anexo); return; }
    // Se tem base64 local, baixa direto
    if (anexo.base64) {
      const link = document.createElement("a");
      link.href = `data:${anexo.tpArquivo||"application/octet-stream"};base64,${anexo.base64}`;
      link.download = anexo.nmArquivo;
      link.click();
    }
  };

  return (
    <div>
      {/* Drop zone */}
      {!disabled && (
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
          onClick={() => inputRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? "var(--primary)" : "var(--gray-300)"}`,
            borderRadius: 8, padding: "14px 16px",
            background: dragging ? "#EFF6FF" : "var(--gray-50)",
            cursor: loading ? "wait" : "pointer",
            display: "flex", alignItems: "center", gap: 10,
            transition: "all .15s", marginBottom: anexos.length ? 10 : 0,
          }}>
          <i className={`bi ${loading ? "bi-hourglass-split" : "bi-cloud-upload"}`}
            style={{ fontSize: 20, color: "var(--primary)" }}/>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--gray-700)" }}>
              {loading ? "Processando..." : label}
            </div>
            <div style={{ fontSize: 11, color: "var(--gray-400)" }}>
              Clique ou arraste arquivos aqui · Máx. {maxSizeMB}MB por arquivo
            </div>
          </div>
          <input ref={inputRef} type="file" multiple accept={accept}
            style={{ display:"none" }}
            onChange={e => { handleFiles(e.target.files); e.target.value=""; }}/>
        </div>
      )}

      {/* Lista de anexos */}
      {anexos.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {anexos.map((a, i) => (
            <div key={a.id ?? i} style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 12px", background:"var(--bg-surface)",
              border: "1px solid var(--gray-200)", borderRadius: 8,
            }}>
              <i className={`bi ${getIcon(a.tpArquivo)}`}
                style={{ fontSize: 20, color: getColor(a.tpArquivo), flexShrink: 0 }}/>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 12.5, color: "var(--gray-800)",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {a.nmArquivo}
                </div>
                {a.tamanho && (
                  <div style={{ fontSize: 11, color: "var(--gray-400)" }}>
                    {fmtSize(a.tamanho)}
                    {a.createdAt && ` · ${new Date(a.createdAt).toLocaleDateString("pt-BR")}`}
                    {a.adicionadoPor && ` · ${a.adicionadoPor}`}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                {/* Download — só se tiver base64 ou onDownload */}
                {(a.base64 || onDownload) && (
                  <button className="btn btn-outline-secondary btn-sm btn-icon"
                    title="Baixar" onClick={() => handleDownload(a)}>
                    <i className="bi bi-download"/>
                  </button>
                )}
                {!disabled && onRemove && (
                  <button className="btn btn-outline-danger btn-sm btn-icon"
                    title="Remover" onClick={() => onRemove(a.id ?? i)}>
                    <i className="bi bi-trash3"/>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!disabled && !loading && anexos.length === 0 && (
        <div style={{ fontSize: 12, color: "var(--gray-400)", textAlign: "center",
          padding: "4px 0" }}>
          Nenhum arquivo anexado
        </div>
      )}
    </div>
  );
}
